import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, X, Save, LogOut, Lock, Radio, CheckCircle2, RotateCcw, Send, Eye, UserCheck } from "lucide-react";
import { FormFieldInput, visibleFields } from "@/components/FormFieldKit";
import { confirmAction } from "@/lib/confirm";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const HEARTBEAT_MS = 30000;
const POLL_MS = 8000;
const AUTOSAVE_MS = 1500;

function SaveState({ state, at }) {
  const map = { saving: ["Saving…", "text-[#9FA1A4]"], dirty: ["Unsaved changes", "text-amber-600"], saved: [at ? `Saved ${fmt(at)}` : "All changes saved", "text-[#1f9d55]"], error: ["Couldn't save", "text-rose-600"] };
  const [label, cls] = map[state] || map.saved;
  return <span data-testid="live-form-save-state" className={`text-xs ${cls} hidden sm:flex items-center gap-1.5 whitespace-nowrap`}>{state === "saving" ? <Loader2 className="h-3 w-3 animate-spin" /> : state === "saved" ? <CheckCircle2 className="h-3 w-3" /> : null}{label}</span>;
}

export function LiveFormEditor({ base, uploadUrl, isAgency, clientName, onClose, onChanged }) {
  const [st, setSt] = useState(null);
  const [values, setValues] = useState({});
  const [editing, setEditing] = useState(false);
  const [saveState, setSaveState] = useState("saved");
  const [busy, setBusy] = useState(null);
  const [uploads, setUploads] = useState(0);
  const valuesRef = useRef({});
  const dirtyRef = useRef(false);
  const editingRef = useRef(false);
  const timer = useRef(null);
  const seq = useRef(0);

  const apply = useCallback((data, takeValues) => {
    seq.current += 1;
    setSt(data);
    if (takeValues) { setValues(data.live.data || {}); valuesRef.current = data.live.data || {}; }
    editingRef.current = data.live.i_hold_lock && data.live.status !== "finalised";
    setEditing(editingRef.current);
  }, []);

  const tryLock = useCallback(async (quiet) => {
    try { const { data } = await api.post(`${base}/lock`); apply(data, true); return true; }
    catch (e) {
      if (e.response?.status === 409) { const { data } = await api.get(base); apply(data, true); if (!quiet) toast.message(formatApiError(e.response?.data?.detail)); }
      else toast.error(formatApiError(e.response?.data?.detail));
      return false;
    }
  }, [base, apply]);

  useEffect(() => {
    api.get(base).then(({ data }) => { apply(data, true); if (data.live.status !== "finalised") tryLock(true); })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose(); });
  }, [base]); // eslint-disable-line

  const save = useCallback(async () => {
    if (!editingRef.current) return false;
    clearTimeout(timer.current);
    setSaveState("saving");
    try {
      const { data } = await api.put(base, { data: valuesRef.current });
      dirtyRef.current = false; setSaveState("saved");
      setSt((s) => (s ? { ...s, live: { ...s.live, last_saved_at: data.last_saved_at } } : s));
      return true;
    } catch (e) {
      setSaveState("error");
      if (e.response?.status === 409) { toast.error(formatApiError(e.response?.data?.detail)); editingRef.current = false; setEditing(false); }
      return false;
    }
  }, [base]);

  useEffect(() => {
    if (!st) return undefined;
    const t = setInterval(async () => {
      if (editingRef.current) { if (dirtyRef.current) await save(); else api.post(`${base}/lock`).catch(() => {}); }
      else {
        const started = seq.current;
        try { const { data } = await api.get(base); if (seq.current === started && !editingRef.current) apply(data, true); } catch { /* ignore */ }
      }
    }, editingRef.current ? HEARTBEAT_MS : POLL_MS);
    return () => clearInterval(t);
  }, [st?.live?.lock?.user_id, editing, base, save, apply]); // eslint-disable-line

  const setVal = (id, v) => {
    const next = { ...valuesRef.current, [id]: v };
    valuesRef.current = next; setValues(next); dirtyRef.current = true; setSaveState("dirty");
    clearTimeout(timer.current); timer.current = setTimeout(save, AUTOSAVE_MS);
  };
  const uploader = async (file) => {
    setUploads((n) => n + 1);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post(uploadUrl, fd, { headers: { "Content-Type": "multipart/form-data" } }); return data; }
    finally { setUploads((n) => Math.max(0, n - 1)); }
  };

  const close = async () => {
    if (editingRef.current) { if (dirtyRef.current) await save(); await api.post(`${base}/unlock`).catch(() => {}); }
    onChanged?.(); onClose();
  };
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" || document.body.dataset.escClose !== "1" || document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      if (editing) saveClose(); else close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const saveClose = async () => { setBusy("close"); if (await save()) { await api.post(`${base}/unlock`).catch(() => {}); toast.success("Saved"); onChanged?.(); onClose(); } setBusy(null); };
  const finalise = async () => {
    if (!(await confirmAction({ title: "Finalise & submit?", message: `This locks ${clientName ? `${clientName}'s` : "the"} live form and records it as a submission. You can reopen it later.`, confirmText: "Finalise & submit", destructive: false }))) return;
    setBusy("final");
    try {
      if (editingRef.current && dirtyRef.current) await save();
      await api.post(`${base}/finalise`); toast.success("Form finalised and submitted");
      const { data } = await api.get(base); apply(data, true); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const reopen = async () => {
    setBusy("reopen");
    try {
      await api.post(`${base}/reopen`);
      seq.current += 1;
      if (!(await tryLock(true))) { const { data } = await api.get(base); apply(data, true); }
      toast.success("Form reopened for editing"); onChanged?.();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };

  useEffect(() => () => clearTimeout(timer.current), []);
  if (!st) return <div className="fixed inset-0 z-50 bg-[#FAFAFD] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#c4c5cc]" /></div>;
  const { form, live, me } = st;
  const finalised = live.status === "finalised";
  const lockedByOther = !finalised && live.lock && !live.i_hold_lock;
  const fields = visibleFields(form.fields || [], values);

  return (
    <div className="fixed inset-0 z-50 bg-[#FAFAFD] flex flex-col" data-testid="live-form-editor">
      <header className="h-16 shrink-0 border-b border-[#EEEFEF] bg-white px-4 md:px-8 flex items-center gap-3">
        <button data-testid="live-form-close" onClick={close} className="h-9 w-9 rounded-full hover:bg-[#F6F6FD] flex items-center justify-center text-[#595D62]"><X className="h-5 w-5" /></button>
        <div className="min-w-0 flex-1">
          <p className="font-display font-semibold text-[#06060C] truncate flex items-center gap-2">{form.title}
            <span className={`font-mono-data text-[9px] uppercase tracking-wider rounded-full px-2 py-0.5 flex items-center gap-1 ${finalised ? "bg-[#1f9d55]/10 text-[#1f9d55]" : "bg-[#EDE7FF] text-[#5b2fd6]"}`}>{finalised ? <><CheckCircle2 className="h-3 w-3" /> Finalised</> : <><Radio className="h-3 w-3" /> Live</>}</span>
          </p>
          <p className="text-[11px] text-[#9FA1A4] truncate">{clientName ? `${clientName} · ` : ""}{live.last_saved_at ? `Last saved ${fmt(live.last_saved_at)} by ${live.last_saved_by}` : "Not started yet"}</p>
        </div>
        {editing && <SaveState state={saveState} at={live.last_saved_at} />}
        {editing && <Button data-testid="live-form-save" variant="outline" onClick={save} disabled={saveState === "saving" || uploads > 0} className="rounded-full border-[#EEEFEF] h-9"><Save className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Save</span></Button>}
        {editing && <Button data-testid="live-form-save-close" onClick={saveClose} disabled={!!busy || uploads > 0} className="rounded-full bg-[#06060C] text-white hover:bg-[#1c1c24] h-9">{busy === "close" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogOut className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Save & close</span></>}</Button>}
        {isAgency && !finalised && <Button data-testid="live-form-finalise" onClick={finalise} disabled={!!busy || lockedByOther || uploads > 0} title={lockedByOther ? "Someone is still editing" : ""} className="rounded-full bg-[#723FFF] text-white hover:bg-[#5b2fd6] h-9">{busy === "final" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Finalise & submit</>}</Button>}
        {isAgency && finalised && <Button data-testid="live-form-reopen" variant="outline" onClick={reopen} disabled={!!busy} className="rounded-full border-[#C9B8FF] text-[#5b2fd6] h-9">{busy === "reopen" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RotateCcw className="h-4 w-4 mr-1.5" /> Reopen</>}</Button>}
      </header>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 md:px-0 py-8 space-y-5">
          <div className="rounded-2xl border border-[#EDE7FF] bg-[#F8F5FF] px-4 py-3 flex items-center gap-3" data-testid="live-form-signed-in">
            <span className="h-9 w-9 rounded-full bg-[#723FFF] text-white flex items-center justify-center text-sm font-semibold shrink-0">{(me.name || "?").charAt(0).toUpperCase()}</span>
            <div className="min-w-0"><p className="text-sm text-[#06060C]">Hi {(me.name || "").split(" ")[0] || "there"}, you're signed in as <span className="font-medium">{me.name}</span></p><p className="text-[11px] text-[#595D62]">{me.email} · your changes are saved under your name{editing ? " and autosave as you type" : ""}.</p></div>
            <UserCheck className="h-4 w-4 text-[#723FFF] ml-auto shrink-0" />
          </div>
          {lockedByOther && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3" data-testid="live-form-locked">
              <Lock className="h-4 w-4 text-amber-700 shrink-0" />
              <p className="text-sm text-amber-800 flex-1"><span className="font-medium">{live.lock.name}</span> is editing right now. You can view the form, and it unlocks for you once they save and close.</p>
            </div>
          )}
          {!lockedByOther && !editing && !finalised && (
            <div className="rounded-2xl border border-[#EEEFEF] bg-white px-4 py-3 flex items-center gap-3" data-testid="live-form-free">
              <Eye className="h-4 w-4 text-[#595D62]" /><p className="text-sm text-[#595D62] flex-1">Viewing only. Nobody is editing now.</p>
              <Button data-testid="live-form-start-editing" size="sm" onClick={() => tryLock(false)} className="rounded-full bg-[#06060C] text-white hover:bg-[#1c1c24]">Start editing</Button>
            </div>
          )}
          {finalised && <div className="rounded-2xl border border-[#1f9d55]/20 bg-[#1f9d55]/5 px-4 py-3 text-sm text-[#166c3b]" data-testid="live-form-finalised">Finalised {fmt(live.finalised_at)} by {live.finalised_by}. {isAgency ? "Reopen it to allow more changes." : "It's now read-only."}</div>}
          <div>
            <h1 className="font-display text-2xl font-semibold text-[#06060C]">{form.title}</h1>
            {form.description && <p className="text-sm text-[#595D62] mt-1">{form.description}</p>}
            {live.contributors.length > 0 && <div className="flex flex-wrap gap-1.5 mt-3" data-testid="live-form-contributors">{live.contributors.map((c) => <span key={c.user_id} className="text-[11px] rounded-full border border-[#EEEFEF] bg-white px-2 py-0.5 text-[#595D62]">{c.name}{c.side === "agency" ? " · agency" : ""}</span>)}</div>}
          </div>
          <fieldset disabled={!editing} className={`rounded-3xl border border-[#EEEFEF] bg-white p-5 md:p-7 space-y-5 ${editing ? "" : "opacity-80"}`} data-testid="live-form-fields">
            {fields.map((fld) => {
              const m = live.field_meta?.[fld.id];
              return (
                <div key={fld.id}>
                  <FormFieldInput field={fld} value={values[fld.id]} onChange={(v) => setVal(fld.id, v)} uploader={uploader} />
                  {m && <p className="text-[10px] text-[#9FA1A4] mt-1" data-testid={`live-form-meta-${fld.id}`}>Last edited by {m.by_name} · {fmt(m.at)}</p>}
                </div>
              );
            })}
            {fields.length === 0 && <p className="text-sm text-[#9FA1A4]">This form has no fields yet.</p>}
          </fieldset>
        </div>
      </div>
    </div>
  );
}
